import { Effect, Layer, Schema } from "effect";
import { HttpClient, HttpClientRequest, type HttpClientResponse } from "effect/unstable/http";
import { HttpApi, HttpApiClient } from "effect/unstable/httpapi";
import { RunDispatchApi } from "@workerstation/shared/runtime/run-dispatch";
import { HostedTestApi } from "@workerstation/shared/hosted-test/api";
import { HttpFailureBody } from "@workerstation/shared/errors/http-body";
import { ResourceRequestError, resourceUnavailable } from "@workerstation/shared/errors/resource";
import { HostedTestHttpError } from "@workerstation/shared/errors/hosted-test-http";
import { ExecutionFailure, PreviewResult } from "./model.ts";
import { ExecutionApi } from "./service.ts";

const Api = HttpApi.make("FrontendExecutionApi").add(RunDispatchApi, HostedTestApi);
const Envelope = Schema.Struct({ error: Schema.optionalKey(Schema.Json) });
// These endpoint errors are pure shared schemas; the public collection exposes
// Top while the endpoint retains its declared Type, as in the station adapter.
const hostedError = (status: number) => Schema.Union([...HostedTestApi.endpoints.legacy.error].filter(schema => schema.ast.annotations?.httpApiStatus === status)) as unknown as Schema.Codec<(typeof HostedTestApi.endpoints.legacy)["~Error"]["Type"], unknown>;
const publicRefusal = (response: HttpClientResponse.HttpClientResponse, hosted: boolean): Effect.Effect<never, ExecutionFailure> => {
  const status = response.status;
  if (hosted) {
    if (status !== 400 && status !== 404 && status !== 405 && status !== 409 && status !== 502 && status !== 503) return Effect.fail(resourceUnavailable());
    return response.json.pipe(Effect.flatMap(Schema.decodeUnknownEffect(hostedError(status))), Effect.mapError(() => resourceUnavailable()),
      Effect.flatMap(body => Effect.fail(new HostedTestHttpError({ status, message: body.error }))));
  }
  if (status !== 400 && status !== 404 && status !== 409) return Effect.fail(resourceUnavailable());
  return response.json.pipe(Effect.flatMap(Schema.decodeUnknownEffect(HttpFailureBody)), Effect.mapError(() => resourceUnavailable()),
    Effect.flatMap(body => Effect.fail(new ResourceRequestError({ status, message: body.error }))));
};
const own = <A, E, R>(operation: Effect.Effect<A, E, R>) => Effect.scoped(operation.pipe(
  Effect.mapError((error): ExecutionFailure => Schema.is(ExecutionFailure)(error) ? error : resourceUnavailable())
));

export const makeExecutionApi = Effect.fn("Frontend.ExecutionApi.make")(function* (baseUrl: string) {
  const http = HttpClient.withScope(yield* HttpClient.HttpClient);
  const json = (hosted: boolean) => HttpClient.transformResponse(http, response => response.pipe(
    Effect.mapError(() => resourceUnavailable()),
    Effect.flatMap(value => value.status >= 200 && value.status < 300 ? value.json.pipe(
      Effect.flatMap(Schema.decodeUnknownEffect(Envelope)), Effect.mapError(() => resourceUnavailable()),
      Effect.flatMap(body => body.error ? Effect.fail(resourceUnavailable()) : Effect.succeed(value))
    ) : publicRefusal(value, hosted))
  ));
  const hosted = yield* HttpApiClient.makeWith(Api, { httpClient: json(true), baseUrl });
  return ExecutionApi.of({
    preview: Effect.fn("Frontend.ExecutionApi.preview")(function* (request) {
      // Validate with the shared endpoint while retaining the native JSON order.
      // A per-call transport keeps concurrent requests' captured bytes isolated.
      const body = yield* Effect.try({ try: () => JSON.stringify(request), catch: () => resourceUnavailable() });
      const preview = yield* HttpApiClient.makeWith(Api, {
        httpClient: HttpClient.mapRequest(json(false), value => HttpClientRequest.bodyText(value, body, "application/json")), baseUrl
      });
      const response = yield* preview.runDispatch.run({ headers: {}, payload: request });
      // The editor requests execution, so a valid build-only response still
      // cannot become a Preview/HTTP/Console result.
      return yield* Schema.decodeUnknownEffect(PreviewResult)(response).pipe(Effect.mapError(() => resourceUnavailable()));
    }, own),
    tests: Effect.fn("Frontend.ExecutionApi.tests")((payload) => hosted.hostedTests.legacy({ payload }), own)
  });
});
export const ExecutionApiLive = (baseUrl: string) => Layer.effect(ExecutionApi, makeExecutionApi(baseUrl));
